<template>
    <v-dialog
        :value="isOpen"
        max-width="500px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="!isNewMaterial">
                        Edycja materiału <strong>{{ material.name }}</strong>
                    </template>
                    <template v-else>
                        Tworzenie materiału
                    </template>
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-text-field
                    v-model="name"
                    label="Nazwa"
                    :error-messages="materialNameErrors"
                    dense
                    required
                    outlined
                />
                <v-select
                    v-model="materialType"
                    label="Typ"
                    :items="materialTypes"
                    item-value="id"
                    item-text="name"
                    return-object
                    no-data-text="Nie znaleziono typu materiału"
                    :error-messages="materialTypeErrors"
                    dense
                    required
                    outlined
                    @change="updateProductProductionProperty()"
                />
                <v-text-field
                    v-model="materialIndex"
                    label="Indeks materiałowy"
                    :error-messages="materialIndexErrors"
                    dense
                    required
                    outlined
                />
                <v-select
                    v-model="unitOfMeasureId"
                    label="Jednostka"
                    :items="unitsOfMeasure"
                    item-value="id"
                    no-data-text="Nie znaleziono jednostek"
                    :error-messages="unitOfMeasureErrors"
                    dense
                    required
                    outlined
                >
                    <template slot="selection" slot-scope="data">
                        {{ data.item.name }}
                    </template>

                    <template slot="item" slot-scope="data">
                        {{ data.item.name }}
                    </template>
                </v-select>
                <v-autocomplete
                    v-model="productProductionProperty"
                    label="Cecha produktu"
                    :items="productProductionProperties"
                    :search-input.sync="search"
                    placeholder="Wybierz cechy produktu"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono cech produktu"
                    chips
                    clearable
                    dense
                    multiple
                    outlined
                    @change="search = null"
                >
                    <template #selection="data">
                        <v-chip
                            close
                            small
                            @update:active="removeProductProductionProperty(data.item)"
                        >
                            {{ data.item.name }}
                        </v-chip>
                    </template>
                </v-autocomplete>
                <v-text-field
                    v-model="piecePrice"
                    label="Cena netto za jednostkę"
                    outlined
                    dense
                    :disabled="!canChangePrice"
                    :error-messages="piecePriceErrors"
                />
                <v-checkbox
                    v-model="isVisible"
                    label="Czy aktywny?"
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    :disabled="actionLoading"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading"
                    @click="saveMaterial"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import priceFormatter from '@/mixins/priceFormatter'
import { isEmpty } from 'lodash'
import { hasUserPermission } from '@/utils/permissionsHandler'
import { errorHandler } from '@/utils/errorHandler'
import { twoDigitsAfterDecimalWithComma } from '@/utils/customValidators'

export default {
    name: 'SaveMaterialDialog',
    mixins: [
        priceFormatter
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        material: {
            type: Object,
            default: null
        },
        productProductionProperties: {
            type: Array,
            default: () => []
        },
        action: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            name: null,
            materialType: {},
            materialIndex: null,
            unitOfMeasureId: null,
            productProductionProperty: [],
            piecePrice: null,
            isVisible: false,
            actionLoading: false,
            search: null
        }
    },
    validations() {
        return {
            name: {
                required
            },
            materialIndex: {
                required
            },
            unitOfMeasureId: {
                required
            },
            materialType: {
                required
            },
            piecePrice: {
                twoDigitsAfterDecimalWithComma
            },
        }
    },
    computed: {
        unitsOfMeasure() {
            return this.$store.state.configurations.materials.unitsOfMeasure
        },
        isNewMaterial() {
            return isEmpty(this.material)
        },
        materialTypes() {
            return this.$store.state.configurations.materials.materialTypes
        },
        canChangePrice() {
            return hasUserPermission('papers_price_update') || this.isNewMaterial
        },
        // Walidacje
        materialNameErrors() {
            const errors = []

            if (!this.$v.name.$dirty) return errors
            !this.$v.name.required && errors.push('Proszę podać nazwę materiału.')

            return errors
        },
        materialIndexErrors() {
            const errors = []

            if (!this.$v.materialIndex.$dirty) return errors
            !this.$v.materialIndex.required && errors.push('Proszę indeks materiałowy.')

            return errors
        },
        unitOfMeasureErrors() {
            const errors = []

            if (!this.$v.unitOfMeasureId.$dirty) return errors
            !this.$v.unitOfMeasureId.required && errors.push('Proszę wybrać jednostkę.')

            return errors
        },
        materialTypeErrors() {
            const errors = []

            if (!this.$v.materialType.$dirty) return errors
            !this.$v.materialType.required && errors.push('Proszę wybrać typ.')

            return errors
        },
        piecePriceErrors() {
            const errors = []

            if (!this.$v.piecePrice.$dirty) return errors
            !this.$v.piecePrice.twoDigitsAfterDecimalWithComma && errors.push('Proszę podać poprawną wartość.')

            return errors
        },
    },
    watch: {
        material(value) {
            if (!isEmpty(value)) {
                this.name = value.name
                this.materialIndex = value.materialIndex
                this.unitOfMeasureId = value.unitOfMeasureId
                this.materialType = value.materialType
                this.isVisible = value.isVisible
                this.productProductionProperty = value.materialType.productProductionProperty
                this.piecePrice = this.formatPrice(value.piecePrice)
            }
        }
    },
    methods: {
        updateProductProductionProperty() {
            this.productProductionProperty = JSON.parse(JSON.stringify(this.materialType.productProductionProperty))
        },
        removeProductProductionProperty(item) {
            this.productProductionProperty.splice(this.productProductionProperty.indexOf(item.id), 1)
        },
        saveMaterial() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                let action = null

                const materialPayload = {
                    name: this.name,
                    materialIndex: this.materialIndex,
                    unitOfMeasureId: this.unitOfMeasureId,
                    materialTypeId: this.materialType.id,
                    piecePrice: this.piecePrice === '' ? 0 : this.unformatPrice(this.piecePrice),
                    isVisible: this.isVisible
                }

                const materialPayloadType = {
                    id:  this.materialType.id,
                    productProductionProperty: this.productProductionProperty
                }

                if (this.isNewMaterial) {
                    action = 'createMaterial'
                } else {
                    action = 'updateMaterial'
                    materialPayload.id = this.material.id
                }

                this.actionLoading = true
                Promise.all([
                    this.$store.dispatch(`configurations/${action}`, materialPayload),
                    this.$store.dispatch('configurations/updateMaterialType', materialPayloadType)
                ])
                    .then(() => {
                        this.$emit('on-save')
                        this.$v.$reset()
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading = false
                        this.onClose()
                    })
            }
        },
        onClose() {
            setTimeout(() => {
                this.name = null
                this.materialIndex = null
                this.materialType = null
                this.unitOfMeasureId = null
                this.materialType = {}
                this.productProductionProperty = []
                this.piecePrice = null
                this.isVisible = false
                this.$v.$reset()
            }, 500)

            this.$emit('on-close')
        }
    }
}
</script>

